import { roles } from "./roles";

/**
 * Agentic-platform (rxConductor) shared constants: the Tasks-hub sections
 * (refresh cadence, HITL task types) and the access gate used by the sidebar,
 * routes, and patient-page surfaces. One file so they can't drift apart.
 */

/**
 * Background auto-refresh cadence (ms). Each section skips the refresh while
 * its tab is hidden or a HITL modal is open, so it never reloads the table
 * out from under the navigator.
 */
export const AUTO_REFRESH_MS = 60000;

/**
 * The navigator pause-points the Tasks hub can open. Mirrors the platform's
 * `AgenticHitlTaskType` enum. The screenshot-upload operator flow is NOT a HITL
 * pause — it lives in the EMR-tasks tab (its own table/endpoint), not here.
 * Shared so the hub page + the HITL modal reference one definition.
 */
export const HITL_TASK_TYPES = Object.freeze({
  EMR_FILL: "emr_fill",
  CONSENT_DECIDE: "consent_decide"
});

/**
 * The pipeline step names the platform writes on `agentic_workflow_run_steps`.
 * Mirrors the platform's `AgenticStepName` enum (tm-agentic-platform) — the wire
 * values must stay byte-identical, since the FE matches audit rows by them.
 * Single definition so the audit modal, the Tasks hub, and the dashboard can't
 * drift from each other or from the platform.
 */
export const AGENTIC_STEP_NAMES = Object.freeze({
  EMR_GATHERING: "emr_gathering",
  EMR_DATA_PARSED: "emr_data_parsed",
  FINANCIAL_ASSISTANCE_ELIGIBILITY: "financial_assistance_eligibility",
  REMAINING_OOP_CALCULATION: "remaining_oop_calculation",
  CONSENT_STATE_CHECK: "consent_state_check",
  UPDATE_PATIENT_STATUS: "update_patient_status",
  UPDATE_HYPERCARE_QUEUE: "update_hypercare_queue",
  SEND_CONSENT: "send_consent",
  CONSENT_HUMAN_REVIEW: "consent_human_review",
  EMR_MANUAL_FILL: "emr_manual_fill"
});

/**
 * `agentic_workflow_run_steps.result` DB enum — a step's technical outcome
 * (`null` while the step is still in progress). Lowercase to match the stored
 * values; a mis-cased lookup would silently read as "skipped" everywhere.
 */
export const AUDIT_STEP_RESULT = Object.freeze({
  PASSED: "passed",
  FAILED: "failed",
  SKIPPED: "skipped"
});

/**
 * Denormalized run status (`agentic_workflow_runs.status`, platform
 * `AgenticRunStatus`). `RUN_STATUS_META` (runStatuses.js) is keyed by these.
 */
export const AGENTIC_RUN_STATUS = Object.freeze({
  RUNNING: "running",
  WAITING_FOR_SCREENSHOTS: "waiting_for_screenshots",
  WAITING_HITL: "waiting_hitl",
  BLOCKED: "blocked",
  SENT: "sent",
  DECLINED: "declined",
  TIMEOUT: "timeout",
  NOT_ELIGIBLE: "not_eligible",
  OOP_MET: "oop_met",
  HAS_ACTIVE_CONSENT: "has_active_consent",
  FAILED: "failed"
});

/**
 * A navigator's decision on a HITL pause (`payload.decision`), plus the
 * workflow-written `timeout` when the wait window elapses with no response.
 */
export const REVIEW_DECISION = Object.freeze({
  APPROVE: "approve",
  DECLINE: "decline",
  TIMEOUT: "timeout"
});

/**
 * Patient contact-field identifiers as they appear on step payloads
 * (`availableContactChannels` / `missingContactFields`) and the consent form.
 */
export const CONTACT_FIELD = Object.freeze({
  EMAIL: "email",
  PHONE_NUMBER: "phoneNumber",
  DOB: "dob"
});

/**
 * `update_hypercare_queue` terminal disposition (`payload.queueStatus`):
 * COMPLETED = the agent resolved + closed the patient; AVAILABLE = handed the
 * patient back to the navigator queue. Defaults to AVAILABLE when absent.
 */
export const QUEUE_STATUS = Object.freeze({
  COMPLETED: "completed",
  AVAILABLE: "available"
});

/**
 * `financial_assistance_eligibility` step outcome (platform
 * FINANCIAL_ASSISTANCE_REASONS). Every not-eligible variant shares
 * {@link NOT_ELIGIBLE_PREFIX} so the UI can group them with one check.
 */
export const FIN_ASSIST_REASON = Object.freeze({
  ELIGIBLE: "eligible",
  NOT_ELIGIBLE_NO_INSURANCE_DATA: "not_eligible_no_insurance_data",
  NOT_ELIGIBLE_NO_REASONS: "not_eligible_no_reasons",
  NO_ACTIVE_MEDICATIONS: "no_active_medications"
});

/** Shared prefix of every `FIN_ASSIST_REASON` not-eligible variant. */
export const NOT_ELIGIBLE_PREFIX = "not_eligible";

/**
 * `emr_gathering` step outcome (platform EmrGatheringExitMode). OPERATOR_DONE +
 * ALREADY_FRESH are terminal successes; the operator_done_* trio +
 * NO_SCREENSHOTS_PROVIDED are failures; the WAITING_/PROCESSING_ pair are the
 * two in-progress sub-phases.
 */
export const EMR_GATHERING_OUTCOME = Object.freeze({
  OPERATOR_DONE: "operator_done",
  ALREADY_FRESH: "already_fresh",
  OPERATOR_DONE_WRONG_PATIENT: "operator_done_wrong_patient",
  OPERATOR_DONE_EXTRACTION_FAILED: "operator_done_extraction_failed",
  OPERATOR_DONE_NO_UPLOADS: "operator_done_no_uploads",
  NO_SCREENSHOTS_PROVIDED: "no_screenshots_provided",
  WAITING_FOR_SCREENSHOTS: "waiting_for_screenshots",
  PROCESSING_SCREENSHOTS: "processing_screenshots"
});

/**
 * Consent state (platform `ConsentReason`) carried on the consent step's
 * `outcome` and — pre-decision — on the consent_human_review step's `outcome`.
 */
export const CONSENT_REASON = Object.freeze({
  ELIGIBLE_TO_SEND: "eligible_to_send",
  PATIENT_HAS_ACTIVE_CONSENT: "patient_has_active_consent",
  CONSENT_LINK_IN_PROGRESS: "consent_link_in_progress",
  CONSENT_LINK_EXPIRED: "consent_link_expired",
  CONSENT_LINK_REVOKED: "consent_link_revoked",
  CONSENT_REVOKED: "consent_revoked",
  CONSENT_EXPIRED: "consent_expired",
  CONTACT_INFO_MISSING: "contact_info_missing"
});

/**
 * `send_consent` step outcome — the happy-path SENT / hard-failure SEND_FAILED,
 * or (when a re-check finds a state shift) one of the {@link CONSENT_REASON} values.
 */
export const SEND_CONSENT_OUTCOME = Object.freeze({
  SENT: "sent",
  SEND_FAILED: "send_failed"
});

/**
 * Run-level final reason (`agentic_workflow_runs.finalOutcomeCode`, platform
 * EnrollToProgramFinalReason) plus the legacy consent codes the audit modal
 * still translates for older rows.
 */
export const RUN_FINAL_REASON = Object.freeze({
  FAILURE: "failure",
  NOT_ELIGIBLE: "not_eligible",
  OOP_MET: "oop_met",
  CONSENT_EXPIRED_OR_REVOKED: "consent_expired_or_revoked",
  CONSENT_SEND_RECOMMENDED: "consent_send_recommended",
  CONSENT_SENT_BY_NAVIGATOR: "consent_sent_by_navigator",
  CONSENT_DECLINED_BY_NAVIGATOR: "consent_declined_by_navigator",
  CONSENT_SEND_BLOCKED_MISSING_INFO: "consent_send_blocked_missing_info",
  CONSENT_SEND_FAILED: "consent_send_failed",
  CONSENT_DECISION_TIMEOUT: "consent_decision_timeout",
  REVIEW_DECLINED: "review_declined",
  REVIEW_TIMEOUT: "review_timeout"
});

/**
 * Roles that can see the agentic-platform surfaces (sidebar entry, the
 * /agentic-platform routes, patient-page runs button). FN's ACL already grants
 * these roles the `agenticplatform` resource server-side — this list is the FE
 * visibility mirror of that.
 */
export const agenticPlatformAllowedRoles = [roles.OPERATION_MANGER, roles.FINANCIAL_NAVIGATOR, roles.INSIGHTS];

/**
 * The agentic-platform access gate: a TMComplete user (TailorMed-employed
 * centralized navigator) whose account ALSO carries one of the allowed roles —
 * both conditions required. Site-level enablement (`tmAgenticPlatformEnabled`)
 * is checked separately by each surface.
 */
export const canAccessAgenticPlatform = (user) =>
  Boolean(user && user.isTMComplete && agenticPlatformAllowedRoles.includes(user.role));
